/* eslint-disable no-return-await */
import * as signalR from '@microsoft/signalr';
import { hubConfigs } from '@/store/hubConfig'; // Import your hub configurations
import { SET_CONNECTION, SET_CONNECTION_STATUS, CLEAR_CONNECTION } from '@/store/types';
import { SIGNALR_TIMEOUTS } from '@/utils/signalRConstants';

const connectionState = {
    connected: 'connected',
    disconnected: 'disconnected',
    connecting: 'connecting'
};

const state = {
    connections: {}, // Stores active connection objects { hubName: connection }
    connectionStatus: {} // Stores status { hubName: 'connected' | 'disconnected' | 'connecting' }
};

const mutations = {
    SET_CONNECTION(state, { hubName, connection }) {
        state.connections[hubName] = connection;
    },
    SET_CONNECTION_STATUS(state, { hubName, status }) {
        state.connectionStatus = { ...state.connectionStatus, [hubName]: status };
    },
    CLEAR_CONNECTION(state, hubName) {
        state.connections[hubName] = null;
    }
};

const actions = {
    async connect({ commit, state }, hubName) {
        if (state.connections[hubName] && state.connectionStatus[hubName] === connectionState.connected) {
            // eslint-disable-next-line no-console
            console.log(`Already connected to ${hubName}.`);
            return;
        }

        const hubConfig = hubConfigs[hubName];
        if (!hubConfig) {
            console.error(`Configuration for hub "${hubName}" not found.`);
            return;
        }
        // Create a new connection with automatic reconnection for stability
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubConfig.url, {
                // Fetch fresh token on each connection/reconnection attempt
                accessTokenFactory: async () => {
                    const userCredentials = await window.auth.getUser();
                    return userCredentials.access_token;
                }
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Retry at 0s, 2s, 5s, 10s, 30s
            .configureLogging(signalR.LogLevel.Information)
            .build();

        // Configure timeouts to match server settings
        connection.serverTimeoutInMilliseconds = SIGNALR_TIMEOUTS.serverTimeoutInMilliseconds;
        connection.keepAliveIntervalInMilliseconds = SIGNALR_TIMEOUTS.keepAliveIntervalInMilliseconds;

        commit(SET_CONNECTION, { hubName, connection });
        commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.connecting });

        connection.onreconnecting((error) => {
            // eslint-disable-next-line no-console
            console.warn(`Connection to ${hubName} lost. Reconnecting...`, error);
            commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.connecting });
        });

        connection.onreconnected(async (connectionId) => {
            // eslint-disable-next-line no-console
            console.log(`Reconnected to ${hubName}. Connection ID: ${connectionId}`);
            commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.connected });
            // Re-join team channel after reconnection to ensure group membership
            try {
                await connection.invoke('JoinToTeamChannel');
                // eslint-disable-next-line no-console
                console.log(`Re-joined team channel for ${hubName} after reconnection.`);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn(`Failed to re-join team channel for ${hubName}:`, err);
            }
        });

        connection.onclose(async (error) => {
            // eslint-disable-next-line no-console
            console.warn(`Connection to ${hubName} closed.`, error);
            commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.disconnected });
        });

        // Start the connection
        try {
            await connection.start();
            commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.connected });
            // eslint-disable-next-line no-console
            console.log(`Successfully connected to ${hubName}.`);
        } catch (err) {
            console.error(`Failed to connect to ${hubName}:`, err);
            commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.disconnected });
        }
    },

    async disconnect({ commit, state }, hubName) {
        const connection = state.connections[hubName];
        if (!connection) 
            return;

        try {
            await connection.stop();
        } catch (err) {
            console.error(`Error disconnecting from ${hubName}:`, err);
        } finally {
            commit(CLEAR_CONNECTION, hubName);
            commit(SET_CONNECTION_STATUS, { hubName, status: connectionState.disconnected });
            // eslint-disable-next-line no-console
            console.log(`Disconnected from ${hubName}.`);
        }
    },

    async registerHandlers({ commit, state }, { hubName, handlers }) {
        const connection = state.connections[hubName];
        if (!connection) {
            console.error(`No connection found for hub "${hubName}".`);
            return;
        }

        handlers.forEach(({ action, handler }) => {
            if (action === 'close') {
                connection.onclose(handler);
            } else {
                connection.on(action, handler);
            }
        });
    },

    async unregisterHandlers({ state }, { hubName, handlers }) {
        const connection = state.connections[hubName];
        if (!connection) {
            console.error(`No connection found for hub "${hubName}".`);
            return;
        }

        handlers.forEach(({ action, handler }) => {
            connection.off(action, handler);
        });
    },

    async joinTeamChannel({ state }, hubName) {
        const connection = state.connections[hubName];
        if (!connection || connection.state !== 'Connected') {
            console.error(`No active connection found for hub "${hubName}".`);
            return;
        }

        try {
            await connection.invoke('JoinToTeamChannel');
            // eslint-disable-next-line no-console
            console.log(`Joined team channel for ${hubName}.`);
        } catch (err) {
            console.error(`Failed to join team channel for ${hubName}:`, err);
        }
    },

    async leaveTeamChannel({ state }, hubName) {
        const connection = state.connections[hubName];
        if (!connection || connection.state !== 'Connected') {
            return;
        }

        try {
            await connection.invoke('RemoveFromTeamChannel');
            // eslint-disable-next-line no-console
            console.log(`Left team channel for ${hubName}.`);
        } catch (err) {
            console.error(`Failed to leave team channel for ${hubName}:`, err);
        }
    }
};

const getters = {
    getConnectionStatus: (state) => (hubName) => {
        return state.connectionStatus[hubName] || connectionState.disconnected;
    },
    getConnection: (state) => (hubName) => {
        return state.connections[hubName];
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
